import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { PageHeader } from "@/components/PageHeader";
import { PageInfo } from "@/components/PageInfo";
import { Deployment, deploymentFromContext, foldDeployment_ } from "@/domain/deployment";
import { DEFAULT_LANGUAGE } from "@/domain/i18n";
import { MenuItem } from "@/domain/menu";
import { fetchMenuItems } from "@/domain/menu.ssr";
import { NewsItem } from "@/domain/news";
import { foldPage_, Page } from "@/domain/page";
import { fetchNews, fetchPage } from "@/domain/page.ssr";
import { useVideo } from "@/hooks/useVideo";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import React from "react";

export const getServerSideProps: GetServerSideProps<{
  page: Page;
  menuItems: Array<MenuItem>;
  newsItem: NewsItem;
  deployment: Deployment;
}> = async (ctx) => {
  const deployment = deploymentFromContext(ctx);
  const locale = ctx.locale ?? DEFAULT_LANGUAGE;
  const id = ctx.params && typeof ctx.params.id === "string" ? ctx.params.id : "NOID";
  const page_id = foldDeployment_(deployment, { ch: () => "service", de: () => "news" });

  const [page, newsItems, menuItems] = await Promise.all([
    fetchPage(deployment, locale, page_id),
    fetchNews(deployment, locale),
    fetchMenuItems(deployment, locale),
  ]);

  const newsItem = newsItems.find((x) => x.id === id);

  if (!page || !newsItem) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      menuItems,
      newsItem,
      deployment,
    },
  };
};

export default function NewsDetailPage({
  page,
  menuItems,
  newsItem,
  deployment,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  useVideo();
  const footerRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <PageInfo title={newsItem.title} deployment={deployment} />
      <div className="layout-sticky__page">
        <header className="site-header site-header--opaque">
          <div className="site-header__content">
            <Header menuItems={menuItems} deployment={deployment} />
          </div>
        </header>
        <section>
          <PageHeader title={newsItem.title} />
          <div className="layout-content group">
            <div className="context-text span-3of5" dangerouslySetInnerHTML={{ __html: newsItem.body }} />
            <div className="context-aside span-2of5 plxxl" dangerouslySetInnerHTML={{ __html: newsItem.aside }} />
          </div>
        </section>
      </div>

      <div
        className={foldPage_(page, {
          default: () => "",
          about: () => "",
          contact: () => "layout-sticky--slim",
          news: () => "",
          organizations: () => "",
        })}
      >
        <div className="layout-sticky__footer" data-spacer-element="footer" ref={footerRef}>
          <Footer
            slim={foldPage_(page, {
              default: () => false,
              about: () => false,
              contact: () => true,
              news: () => false,
              organizations: () => false,
            })}
          />
        </div>
      </div>
    </>
  );
}
